import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Alert, Button } from '@trussworks/react-uswds';

import { ModalContent, ModalFooter } from '@components/modal';
import AddressValidator from '@components/address-validator/AddressValidator';
import { salutations, countries } from '@forms/input-forms/helpers';

import SelectInput from '@components/select/Select';
import TextInput from '@components/text-input/TextInput';
import MultiSelectInput from '@components/multi-select/multiSelectInput';
import Tooltip from '@components/tooltip/tooltip';
import PhoneInput from '@components/phone-input/PhoneInput';
import AddressSuggestInput from '@components/address-suggestion/AddressSuggestInput';
import LinkButton from '@components/link/linkButton';
import ErrorSummary from '@components/error-summary/ErrorSummary';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';

import countryRegionData from 'country-region-data/dist/data-umd';
import { ErrorMessages, ProfileRoles, statesData } from '@src/utils/enums';
import { emailRegex } from '@src/utils/regex';
import { formatUSPhoneNumber, mapCountryAlpha3toAlpha2, mapStateRegulation } from '@src/utils/helpers';
import {
  cleanProfileData,
  getAgencyType,
  getOrganizations,
  shouldSaveOrganizationsForRole,
  getOrganizationIds,
} from './profileHelper';
import { profileValidation } from './inputValidation.profile';
import { contactInputValidation } from '@src/utils/validation/inputValidation.contact';

import { useMutation } from '@tanstack/react-query';
import { ProfileMutations } from '@api/services/profile';
import { useDivisionsAndDistrictsOrganizations } from '@api/services/lookup/hooks';
import { queryClient } from '@api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '@api/services/dashboard';
import { useUserProfile, useAuth } from '@hooks';
import { useGeospatial, useModal } from '@stores';
import Section from '@components/section/Section';
import ExternalLink from '@src/app-components/external-link/ExternalLink';
import { resetRoleData } from '@api/services/user-role-capabilities/helper';
import UsaceOrganization from '@forms/components/UsaceOrganization';
import RadioButton from '@src/app-components/radio-button/RadioButton';
import LabelValue from '@src/app-components/label-value/LabelValue';

import './profile.scss';

const usaceOptions = [
  {
    role: ProfileRoles.USACERegulator,
    description: `I'm a regulatory project manger for USACE.`,
    helperText:
      'Choose this if you will be providing pre-submittal help to applicants and agents on behalf of your USACE district.',
  },
];

const nonUsaceOptions = [
  {
    role: ProfileRoles.Applicant,
    description: `I'm submitting requests for my project or my organization's project.`,
    helperText: 'Choose this if you or your organization are responsible for the proposed project.',
  },
  {
    role: ProfileRoles.Agent,
    description: `I'm submitting request on behalf of someone else.`,
    helperText:
      'Choose this if you are representing an applicant, such as a consultant, engineer, or other authorized representative.',
  },
];

const schema = yup.object().shape(
  {
    role: yup
      .string()
      .required(ErrorMessages.SelectOption)
      .test('isExternalAgency', ErrorMessages.SelectOption, (val) => val !== ProfileRoles.ExternalAgency),
    ...profileValidation.nameRequired,
    companyName: yup.string().nullable(),
    agencyName: yup.string().when('role', {
      is: ProfileRoles.ExternalAgency,
      then: (schema) => schema.required(ErrorMessages.Required),
      otherwise: (schema) => schema.nullable(),
    }),
    agencyType: yup.array().when('role', {
      is: ProfileRoles.ExternalAgency,
      then: (schema) => schema.min(1, ErrorMessages.SelectOption).required(ErrorMessages.SelectOption),
      otherwise: (schema) => schema.nullable(),
    }),
    ...profileValidation.phoneRequired,
    ...contactInputValidation.faxOptional,
    emailAddress: yup.string().when('role', {
      is: (role) => role !== null && role !== '',
      then: (schema) => schema.required(ErrorMessages.Required).matches(emailRegex, ErrorMessages.Invalid),
      otherwise: (schema) => schema.nullable(),
    }),
    ...profileValidation.addressRequired,
    ...profileValidation.organizationsRequired,
  },
  [contactInputValidation.faxOptionalDependencies]
);

const ProfileModal = ({ edit }) => {
  const { userProfileData } = useUserProfile();
  const { logout, profile } = useAuth();
  const { setAddressCandidates } = useGeospatial();
  const hasResetDefaultValues = useRef(false);
  const didOrganizationsChangeRef = useRef(false);
  const resetOrganizationChangeState = () => {
    didOrganizationsChangeRef.current = false;
  };
  const [showPhoneTwo, setShowPhoneTwo] = useState(false);
  const [stateOptions, setStateOptions] = useState([userProfileData?.state]);
  const userEmail = userProfileData?.emailAddress ?? profile.email;
  const isUsaceEmail = /@usace.army.mil\s*$/.test(userEmail) ? true : false;
  const isExternalAgency = userProfileData?.role === ProfileRoles.ExternalAgency;
  const baseRoleOptions = isUsaceEmail ? usaceOptions : nonUsaceOptions;
  const roleOptions = userProfileData?.canUseDeveloperRole
    ? [...baseRoleOptions, { role: ProfileRoles.Developer }]
    : baseRoleOptions;
  const disableRoleSelection = !userProfileData?.canUseDeveloperRole && !!userProfileData?.role;

  const {
    isLoading: isDivisionsAndDistrictsLoading,
    organizationOptions,
    headquartersOption,
    divisionOptions,
    districtOptions,
  } = useDivisionsAndDistrictsOrganizations();

  const selectedOrganizations = useMemo(
    () => getOrganizations(userProfileData?.organizations, organizationOptions),
    [userProfileData?.organizations, organizationOptions]
  );

  const hasOrganizations = !!userProfileData?.organizations?.length;
  const hasOrganizationOptions = organizationOptions?.length > 0;

  const { mutateAsync: saveProfile } = useMutation({
    mutationFn: ProfileMutations.saveUserProfile,
    onSuccess: () => useModal.getState().closeModal(),
  });
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: ProfileMutations.updateUserProfile,
    onSuccess: async (_data, variables) => {
      const didRoleChange = !!variables?.role && variables.role !== userProfileData?.role;
      if (didRoleChange) {
        resetRoleData();
      } else if (variables?.role === ProfileRoles.USACERegulator && didOrganizationsChangeRef.current) {
        await queryClient.invalidateQueries({
          queryKey: [DASHBOARD_QUERY_KEYS.GET_REGULATOR_HELP_REQUESTS],
        });
      }
      resetOrganizationChangeState();
      useModal.getState().closeModal();
    },
    onError: resetOrganizationChangeState,
  });

  const defaultValues = useMemo(() => {
    return {
      role: userProfileData?.role
        ? userProfileData?.role === ProfileRoles.ExternalAgency
          ? null
          : userProfileData?.role
        : null,
      salutation: userProfileData?.salutation ?? null,
      firstName: userProfileData?.firstName ? userProfileData.firstName : profile?.given_name,
      middleName: userProfileData?.middleName ?? null,
      lastName: userProfileData?.lastName ? userProfileData.lastName : profile?.family_name,
      companyName: userProfileData?.companyName ?? null,
      agencyName: userProfileData?.agencyName ?? null,
      phoneOneType: userProfileData?.phoneOneType ?? '',
      phoneOne: userProfileData?.phoneOne
        ? userProfileData?.phoneOneCountryCode === '1'
          ? formatUSPhoneNumber(userProfileData.phoneOne)
          : userProfileData.phoneOne
        : '',
      phoneOneCountryCode:
        userProfileData?.role === ProfileRoles.USACERegulator ? '' : (userProfileData?.phoneOneCountryCode ?? '1'),
      phoneOneExtension: userProfileData?.phoneOneExtension ?? '',
      phoneTwoType: userProfileData?.phoneTwoType ?? '',
      phoneTwo: userProfileData?.phoneTwo
        ? userProfileData?.phoneTwoCountryCode === '1'
          ? formatUSPhoneNumber(userProfileData.phoneTwo)
          : userProfileData.phoneTwo
        : '',
      phoneTwoCountryCode:
        userProfileData?.role === ProfileRoles.USACERegulator ? '' : (userProfileData?.phoneTwoCountryCode ?? '1'),
      phoneTwoExtension: userProfileData?.phoneTwoExtension ?? '',
      faxCountryCode:
        userProfileData?.role === ProfileRoles.USACERegulator ? '' : (userProfileData?.faxCountryCode ?? '1'),
      faxPhone: userProfileData?.faxPhone
        ? userProfileData?.faxCountryCode === '1'
          ? formatUSPhoneNumber(userProfileData.faxPhone)
          : userProfileData.faxPhone
        : '',
      emailAddress: userEmail ?? null,
      agencyType: getAgencyType(userProfileData?.agencyType),
      address: userProfileData?.address ?? null,
      addressTwo: userProfileData?.addressTwo ?? null,
      city: userProfileData?.city ?? null,
      state: userProfileData?.state ?? null,
      zipcode: userProfileData?.zipcode ?? null,
      country: userProfileData?.role === ProfileRoles.USACERegulator ? null : (userProfileData?.country ?? 'US'),
      usaceOrganizations: selectedOrganizations,
    };
  }, [profile, userProfileData, userEmail, selectedOrganizations]);

  const methods = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
    mode: 'onBlur',
    stateOptions: [],
    shouldUnregister: !userProfileData?.role,
  });
  const {
    formState: { errors, isValid },
    setValue,
    setFocus,
    watch,
    getValues,
    getFieldState,
    trigger,
    reset,
  } = methods;

  const role = watch('role');
  const address = watch('address');
  const address2 = watch('addressTwo');
  const city = watch('city');
  const state = watch('state');
  const zipcode = watch('zipcode');
  const country = watch('country');
  const phoneOne = watch('phoneOne');
  const phoneTwoType = watch('phoneTwoType');
  const phoneTwo = watch('phoneTwo');
  const faxPhone = watch('faxPhone');

  const states = mapStateRegulation(statesData);
  const requiresUsaceOrganization = role === ProfileRoles.ExternalAgency || role === ProfileRoles.USACERegulator;

  const handleSave = async () => {
    if (!isValid) {
      await trigger();
      return;
    }
    const values = getValues();
    const { usaceOrganizations, ...profileValues } = values;

    const agencyTypes = values?.agencyType?.map((val) => val.value) ?? [];
    const cleanValues = cleanProfileData(profileValues);

    const shouldSaveOrganizations = shouldSaveOrganizationsForRole(values?.role);
    const organizations = getOrganizationIds(usaceOrganizations);

    const mappedValues = {
      ...profileValues,
      ...cleanValues,
      ...(shouldSaveOrganizations ? { organizations } : {}),
      agencyType: agencyTypes,
      phoneOne: values?.phoneOne?.replace(/\D/g, '') ?? undefined,
      phoneTwo: values?.phoneTwo?.replace(/\D/g, '') ?? undefined,
      faxPhone: values?.faxPhone?.replace(/\D/g, '') ?? undefined,
    };

    if (edit || isExternalAgency) {
      didOrganizationsChangeRef.current = getFieldState('usaceOrganizations').isDirty;

      await updateProfile(mappedValues);
      return;
    }
    await saveProfile(mappedValues);
  };

  const handleBlur = (e) => {
    const str = e?.target?.value.trim();
    const trimmedStr = str?.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    setValue(e?.target?.name, trimmedStr);
  };

  const handleCancel = () => {
    (!edit || userProfileData?.role === ProfileRoles.ExternalAgency) && logout();
  };

  const regionHelper = useCallback((country) => {
    const selectedCountryData = countryRegionData?.find((regionCountry) => regionCountry.countryShortCode === country);

    let regions = [];
    if (selectedCountryData) {
      regions = selectedCountryData.regions.map((region) => ({
        value: region.shortCode ?? region.name, // Some countries do not have provided shortcodes
        text: region.name,
      }));
    }

    regions.push({ value: 'Other', text: 'Other' });
    regions.push({ value: 'None', text: 'None' });

    return regions;
  }, []);

  const handleCountryChange = useCallback(
    (selectedCountry) => {
      const regions = regionHelper(selectedCountry);
      setStateOptions(regions);

      if (selectedCountry !== country) {
        trigger('address');
        setValue('city', null, { shouldValidate: true });
        setValue('state', null, { shouldValidate: true });
        setValue('zipcode', null, { shouldValidate: true });
      }

      if (selectedCountry === 'US' && !phoneOne) {
        setValue('phoneOneCountryCode', '1', { shouldValidate: true });
      } else if (selectedCountry !== 'US' && !phoneOne) {
        setValue('phoneOneCountryCode', '', { shouldValidate: true });
      }

      if (selectedCountry === 'US' && !phoneTwo && showPhoneTwo) {
        setValue('phoneTwoCountryCode', '', { shouldValidate: true });
      } else if (selectedCountry !== 'US' && !phoneTwo && showPhoneTwo) {
        setValue('phoneTwoCountryCode', '', { shouldValidate: true });
      }

      if (selectedCountry === 'US' && !faxPhone) {
        setValue('faxCountryCode', '1', { shouldValidate: true });
      } else if (selectedCountry !== 'US' && !faxPhone) {
        setValue('faxCountryCode', '', { shouldValidate: true });
      }
    },
    [country, phoneOne, phoneTwo, showPhoneTwo, faxPhone, regionHelper, setStateOptions, setValue, trigger]
  );

  const handleRoleOnChange = (e) => setValue('role', e?.target?.value, { shouldValidate: true });

  const populateAddressFields = (address) => {
    const fields = address?.candidates?.[0]?.attributes;
    const addressField = document.getElementById('address');
    const addressFieldFocused = document.activeElement === addressField;

    if (fields) {
      if (addressFieldFocused) {
        addressField.blur();
      }

      setValue('address', fields.StAddr, { shouldValidate: true });
      setValue('addressTwo', fields.SubAddr, { shouldValidate: true });
      setValue('city', fields.City, { shouldValidate: true });
      setValue('state', fields.RegionAbbr, { shouldValidate: true });
      setValue('zipcode', fields.Postal, { shouldValidate: true });
      setValue('country', mapCountryAlpha3toAlpha2(fields.Country), { shouldValidate: true });
      setAddressCandidates([]);

      if (addressFieldFocused) {
        addressField.focus();
      }
    }
  };

  const togglePhone = () => {
    if (showPhoneTwo) {
      setShowPhoneTwo(false);
      setValue('showPhoneTwo', false);
      setValue('phoneTwo', '', { shouldValidate: true });
      setValue('phoneTwoCountryCode', '', { shouldValidate: true });
      setValue('phoneTwoType', '', { shouldValidate: true });
      setValue('phoneTwoExtension', '', { shouldValidate: true });
    } else {
      setShowPhoneTwo(true);
      setValue('showPhoneTwo', true, { shouldValidate: true });
      setValue('phoneTwoCountryCode', country === 'US' ? '1' : '', { shouldValidate: true });
    }
  };

  useEffect(() => {
    setFocus(errors?.[Object.keys(errors)[0]]?.['ref']?.['id']);
  }, [errors, setFocus]);

  useEffect(() => {
    edit && trigger();
  }, [edit, trigger]);

  // Update stateOptions
  useEffect(() => {
    const regions = regionHelper(country);
    setStateOptions(regions);
  }, [country, regionHelper, setStateOptions]);

  // Load state value from DB
  useEffect(() => {
    const matchingOption = stateOptions?.find((option) => option?.value === userProfileData?.state);
    if (matchingOption) {
      setValue('state', matchingOption?.value);
    }
  }, [userProfileData?.state, stateOptions, setValue]);

  // Set showPhoneTwo
  useEffect(() => {
    if ((phoneTwoType || phoneTwo) && !showPhoneTwo) {
      setShowPhoneTwo(true);
      setValue('showPhoneTwo', true);
    }
  }, [phoneTwoType, phoneTwo, showPhoneTwo, setValue]);

  // Rehydrate defaultValues
  useEffect(() => {
    if (userProfileData === undefined || hasResetDefaultValues.current) {
      return;
    }
    if (hasOrganizations && !hasOrganizationOptions) {
      return;
    }
    reset(defaultValues);
    hasResetDefaultValues.current = true;
  }, [reset, userProfileData, defaultValues, hasOrganizations, hasOrganizationOptions]);

  return (
    <ModalContent title='User Profile'>
      <FormProvider {...methods}>
        {errors && <ErrorSummary errors={errors} modalID='profileModal' type='modal' />}
        <section className='modal-body' id='profileModal'>
          <div>
            <Section>
              {userProfileData?.role ? (
                <>
                  <Alert type='warning' slim>
                    A user profile is required to access RRS. Once created, your Email Address and Role cannot be
                    directly updated using this form. If you wish to update your email address, we instead advise
                    creating a new account through our authentication partner{' '}
                    <ExternalLink
                      href='https://secure.login.gov/sign_up/enter_email'
                      title='Login.gov'
                      content='Login.gov'
                      aria-label='Login.gov'
                    />{' '}
                    and transferring ownership of your requests to the new account. For assistance with switching your
                    role, please contact the RRS help desk at{' '}
                    <LinkButton
                      onClick={(e) => {
                        window.location.href = 'mailto:rrs@usace.army.mil?subject=RRS%20Support%20Ticket';
                        e?.preventDefault();
                      }}
                      title='rrs@usace.army.mil'
                      content='rrs@usace.army.mil'
                    />
                    .
                  </Alert>
                  <div className='grid-row grid-gap-1'>
                    {disableRoleSelection ? (
                      <div className='grid-col-12 tablet:grid-col-4 margin-top-3'>
                        <div className='pn-flex-item'>
                          <LabelValue label='Role' value={userProfileData?.role} />
                        </div>
                      </div>
                    ) : (
                      <div className='grid-col-4'>
                        <SelectInput name='role' label='Role' required>
                          {roleOptions.map((item, index) => (
                            <option key={index + 2} value={item.role}>
                              {item.role}
                            </option>
                          ))}
                        </SelectInput>
                      </div>
                    )}
                    <div className='grid-col-12 tablet:grid-col-6 margin-top-3'>
                      <div className='pn-flex-item'>
                        <LabelValue label='Email Address' value={userProfileData?.emailAddress} />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className='grid-row grid-gap-1'>
                    <div className='grid-col-6'>
                      <TextInput
                        name='emailAddress'
                        label='Email'
                        type='email'
                        maxLength={256}
                        required
                        readOnly
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>
                  <div className='grid-row grid-gap-1 margin-top-3'>
                    <div className='grid-col-12'>
                      <fieldset id='roleInputType'>
                        <legend className='usa-label required' htmlFor='role'>
                          Role
                        </legend>
                        <Alert
                          type='warning'
                          heading='Choose your role carefully.'
                          headingLevel='h5'
                          className='margin-bottom-2 margin-top-0'
                        >
                          You cannot update your role from your profile. If you need to change your role later, please
                          contact the RRS Help Desk at{' '}
                          <LinkButton
                            onClick={(e) => {
                              window.location.href = 'mailto:rrs@usace.army.mil?subject=RRS%20Support%20Ticket';
                              e?.preventDefault();
                            }}
                            title='rrs@usace.army.mil'
                            content='rrs@usace.army.mil'
                          />
                        </Alert>
                        {roleOptions.map((item, index) => (
                          <RadioButton
                            key={index}
                            id={`radio-role-${item.role}`}
                            label={<span className='text-bold radio-label'>{item.role}</span>}
                            labelDescription={
                              <>
                                <p className='radio-description'>{item.description}</p>
                                <p className='radio-helper-text'>{item.helperText}</p>
                              </>
                            }
                            name='role'
                            onChange={handleRoleOnChange}
                            value={item.role}
                            checked={role === item.role}
                            tile
                          />
                        ))}
                      </fieldset>
                    </div>
                  </div>
                </>
              )}
            </Section>

            {(role || userProfileData?.role === ProfileRoles.ExternalAgency) && (
              <>
                <Section isSubsection label='Name'>
                  <div className='grid-row grid-gap-1'>
                    <div className='grid-col-4'>
                      <SelectInput name='salutation' label='Salutation' required>
                        {salutations.map((item, i) => (
                          <option key={i + 2} value={item?.value}>
                            {item?.text}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                  </div>
                  <div className='grid-row grid-gap-1'>
                    <div className='grid-col-4'>
                      <TextInput name='firstName' label='First Name' type='text' required />
                    </div>
                    <div className='grid-col-4'>
                      <TextInput name='middleName' label='Middle Name' type='text' onBlur={handleBlur} />
                    </div>
                    <div className='grid-col-4'>
                      <TextInput name='lastName' label='Last Name' type='text' required />
                    </div>
                  </div>
                </Section>
                {role !== ProfileRoles.USACERegulator && (
                  <Section
                    isSubsection
                    label={
                      <>
                        {role === ProfileRoles.ExternalAgency
                          ? 'Agency'
                          : role === ProfileRoles.USACERegulator
                            ? 'Employee'
                            : 'Company'}{' '}
                        Information
                      </>
                    }
                  >
                    {role === ProfileRoles.ExternalAgency && (
                      <div>
                        <MultiSelectInput
                          name='agencyType'
                          label='Agency Type'
                          required={role === ProfileRoles.ExternalAgency}
                          options={[
                            { value: 'Commenting Agency', label: 'Commenting Agency' },
                            { value: 'Consulting Agency', label: 'Consulting Agency' },
                            { value: 'Certifying Agency', label: 'Certifying Agency' },
                          ]}
                          multi
                        />
                      </div>
                    )}
                    <div>
                      <TextInput
                        name={role === ProfileRoles.ExternalAgency ? 'agencyName' : 'companyName'}
                        label={role === ProfileRoles.ExternalAgency ? 'Agency Name' : 'Company Name'}
                        type='text'
                        required={role === ProfileRoles.ExternalAgency}
                        onBlur={handleBlur}
                      />
                    </div>
                    <div className='phone-container phone1'>
                      <PhoneInput
                        extensionName='phoneOneExtension'
                        label='Phone Number'
                        phoneName='phoneOne'
                        phoneTypeName='phoneOneType'
                        phoneCountryCodeName='phoneOneCountryCode'
                        required
                        showTypeSelect
                      />
                      {!showPhoneTwo && (
                        <div className='phone-button-container'>
                          <Button
                            className='button-small add-phone-button'
                            size='small'
                            title='Add Phone Number'
                            id='phone-toggle-1'
                            name='phoneToggleButton1'
                            onClick={togglePhone}
                            onKeyUp={(e) => {
                              e.key === 'Enter' && togglePhone();
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      )}
                    </div>
                    {showPhoneTwo && (
                      <div className='phone-container phone2'>
                        <PhoneInput
                          extensionName='phoneTwoExtension'
                          phoneName='phoneTwo'
                          phoneCountryCodeName='phoneTwoCountryCode'
                          phoneTypeName='phoneTwoType'
                          required
                          showTypeSelect
                        />
                        <div className='phone-button-container'>
                          <Button
                            className='button-small'
                            title='Remove Phone Number'
                            id='phone-toggle-2'
                            name='phoneToggleButton2'
                            onClick={togglePhone}
                            secondary
                            onKeyUp={(e) => {
                              e.key === 'Enter' && togglePhone();
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className='phone-container'>
                      <PhoneInput phoneName='faxPhone' phoneCountryCodeName='faxCountryCode' label='Fax Number' />
                    </div>
                    <div className='margin-top-4'>
                      <AddressSuggestInput
                        id='address'
                        name='address'
                        label='Address'
                        required
                        usOnly={false}
                        onBlur={handleBlur}
                        populateAddressFields={populateAddressFields}
                      />
                    </div>
                    <div>
                      <TextInput name='addressTwo' label='Address Line 2' type='text' onBlur={handleBlur} />
                    </div>
                    <div>
                      <SelectInput
                        name='country'
                        label='Country'
                        required
                        onChange={(e) => handleCountryChange(e.target.value)}
                      >
                        {countries.map((item, i) => (
                          <option key={i + 2} value={item?.value}>
                            {item?.text}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                    <div className='grid-row grid-gap-1'>
                      <div className='grid-col-4'>
                        <TextInput name='city' label='City' type='text' maxLength={26} required onBlur={handleBlur} />
                      </div>
                      {country === 'US' ? (
                        <div className='grid-col-4'>
                          <SelectInput name='state' label='State' required>
                            {states?.map((item, i) => (
                              <option key={i + 2} value={item?.value}>
                                {item?.text}
                              </option>
                            ))}
                          </SelectInput>
                        </div>
                      ) : (
                        <div className='grid-col-4'>
                          <SelectInput name='state' label='Region' required>
                            {stateOptions?.map((item, i) => (
                              <option key={i + 2} value={item?.value}>
                                {item?.text}
                              </option>
                            ))}
                          </SelectInput>
                        </div>
                      )}
                      <div className='grid-col-4'>
                        <TextInput
                          type='tel'
                          inputMode={'numeric'}
                          name='zipcode'
                          label={country === 'US' ? 'Zip Code' : 'Postal code'}
                          required={country === 'US'}
                          maxLength={country === 'US' ? 5 : 10}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    <div className='margin-top-1'>
                      <AddressValidator
                        source='modal'
                        address={{
                          address1: address,
                          address2: address2,
                          city: city,
                          state: state,
                          zipcode: zipcode,
                        }}
                      />
                    </div>
                  </Section>
                )}
                {(role === ProfileRoles.ExternalAgency || role === ProfileRoles.USACERegulator) && (
                  <Section
                    isSubsection
                    label={
                      <>
                        USACE Organization
                        <Tooltip
                          name='Organization'
                          content={
                            <p>
                              Select the USACE organization you support. Choose Headquarters only if you support
                              requests at the HQ level. Division selections cover that division, while district
                              selections apply only to the selected districts.
                            </p>
                          }
                        />
                      </>
                    }
                  >
                    <UsaceOrganization
                      headquartersOption={headquartersOption}
                      divisionOptions={divisionOptions}
                      districtOptions={districtOptions}
                      disabled={isDivisionsAndDistrictsLoading}
                      required={requiresUsaceOrganization}
                    />
                  </Section>
                )}
              </>
            )}
          </div>
        </section>
      </FormProvider>
      <ModalFooter
        customClosingLogic
        isDisabled={!isValid || (requiresUsaceOrganization && isDivisionsAndDistrictsLoading)}
        onCancel={handleCancel}
        onSave={handleSave}
        saveText={edit ? 'Apply Changes' : 'Save'}
      />
    </ModalContent>
  );
};

export default ProfileModal;
